import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { ApiService } from './api.service';
import { Strings } from '@app/shared/constants';
import { map, shareReplay, tap } from 'rxjs/operators';
import { User } from '@app/shared/models/user';

@Injectable({
  providedIn: 'root',
})
export class RegionService extends ApiService {
  private regionTypes$?: Observable<RegionType[]>;
  poHoldingRegion: Region = {} as Region;

  constructor(public http: HttpClient) {
    super(http);
  }

  baseUri = 'socrates/authz/api/regions';
  dbColumnNames = ['Guid', 'RegionTypeId', 'ParentRegionId', 'Name', 'Description', 'Code', 'IsActive', 'CreationDate', 'ModifyDate', 'ModifyUser', 'PKID'];

  public getRegionTypeByCode(code: string): Observable<RegionType[]> {
    return this.http.get<RegionType[]>(this.endpoint('socrates/authz/api/references/regiontypes', `?$filter=code eq '${code}'`));
  }

  public getRegionTypes(): Observable<RegionType[]> {
    if (!this.regionTypes$) {
      this.regionTypes$ = this.http
        .get<RegionType[]>(this.endpoint('socrates/authz/api/references/regiontypes'))
        .pipe(shareReplay({ bufferSize: 1, refCount: true }));
    }
    return this.regionTypes$;
  }

  public getById(guid: string): Observable<Region> {
    if (guid.toLowerCase() === Strings.poHoldingRegionid.toLowerCase() && this.poHoldingRegion !== null) {
      return of(this.poHoldingRegion);
    }

    return this.http.get<Region[]>(this.endpoint(this.baseUri, guid)).pipe(
      map((a: any) => {
        return a.length > 0 ? a[0] : null;
      }),
      tap((d) => {
        if (guid.toLowerCase() === Strings.poHoldingRegionid.toLowerCase()) {
          this.poHoldingRegion = d;
        }
      })
    );
  }

  public getByRegionNode(rootNode: string, selectableNode: string): Observable<Region[]> {
    return this.http.get<Region[]>(this.endpoint(this.baseUri, `?$filter=regionTypeId eq ${rootNode} or regionTypeId eq ${selectableNode}`));
  }

  public getByParentRegionIds(regionIds: string[]): Observable<Region[]> {
    const subqueries: Array<string> = [];
    regionIds.forEach((regionId) => subqueries.push(`parentRegionId eq ${regionId}`));

    return this.http.get<Region[]>(this.endpoint(this.baseUri, `?$filter=${subqueries.join(' or ')}`));
  }

  public getByRegionTypeId(guid: string): Observable<Region[]> {
    return this.http.get<Region[]>(this.endpoint(this.baseUri, `?$filter=regionTypeId eq ${guid}`));
  }

  public getByRegionType(regionType: string): Observable<Region[]> {
    return this.http.get<Region[]>(this.directEndpoint('api', 'userregistration', `adminregions`));
  }

  public getKamEmailByRegionId(id: string): Observable<User[]> {
    return this.http.get<User[]>(this.endpoint('api', 'userregistration', `region/${id}/user`));
  }

  public getByRegionNameAndRegionType(regionName: string, regionTypes: string[]): Observable<Region[]> {
    regionTypes = regionTypes.map((a) => `${a}`);

    const subqueries: Array<string> = [];
    regionTypes.forEach((regionType) => subqueries.push(`regionTypeId eq ${regionType}`));

    return this.http.get<Region[]>(this.endpoint(this.baseUri, `?$filter=(${subqueries.join(' or ')}) and name eq '${regionName}'`));
  }

  public getRegions(): Observable<Region[]> {
    return this.http.get<Region[]>(this.endpoint(this.baseUri));
  }

  public getAllRegions(query?: string): Observable<Region[]> {
    return this.http.get<Region[]>(this.endpoint(this.baseUri, query));
  }

  public getByGuids(regionIds: string[]): Observable<Region[]> {
    const subqueries: Array<string> = [];
    regionIds.forEach((regionId) => subqueries.push(`guid eq ${regionId}`));

    return this.http.get<Region[]>(this.endpoint(this.baseUri, `?$filter=${subqueries.join(' or ')}`));
  }

  public post(data: any): Observable<Region> {
    return this.http.post<Region>(this.endpoint(this.baseUri), data);
  }

  public put(id: string, data: any): Observable<Region> {
    const payload = this.patchMapColumnNames(data, this.dbColumnNames);
    return this.http.patch<Region>(this.endpoint(this.baseUri, id), payload);
  }

  public delete(id: string): Observable<Region> {
    return this.http.delete<Region>(this.endpoint(this.baseUri, id));
  }
}

export interface Region {
  guid: string;
  name: string;
  code: string;
  isActive: boolean;
  regionTypeId: string;
  parentRegionId: string;
}

export interface RegionType {
  guid: string;
  name: string;
  code: string;
  data: any;
}
